import React, { useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import VerifiedIcon from '@mui/icons-material/Verified';
import { DESIGN_TOKENS } from '../theme';

// Geometry — right panel 60%, cards +20% on top of previous
const DIAGRAM_W = 860;
const HUB_W = 240;
const HUB_H = 68;
const HUB_CX = DIAGRAM_W / 2;           // 430
const HUB_LEFT = HUB_CX - HUB_W / 2;   // 310
const ARM_Y0 = HUB_H;                   // arms start at hub bottom
const CONNECTOR_H = 40;

// 5 cards: width 160, gap ~16px → 5×160 + 4×15 = 860
const CARD_W = 160;
const CARD_MIN_H = 128;
const CARD_CY = ARM_Y0 + 240;           // 308 — card centre-y
const CARD_TOP = CARD_CY - CARD_MIN_H / 2; // 244
const CARD_CXS = [80, 255, 430, 605, 780];
const DIAGRAM_H = CARD_TOP + 270;       // room for expanded cards

const ACTION_STEPS = [
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 20, color: '#8b5cf6' }} />,
    iconBg: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
    accent: '#8b5cf6',
    title: 'Reads & organises',
    desc: 'Mill reads every PDF, extracts expiry dates and property addresses, and builds your compliance portfolio automatically.',
  },
  {
    icon: <NotificationsActiveIcon sx={{ fontSize: 20, color: '#0ea5e9' }} />,
    iconBg: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
    accent: '#0ea5e9',
    title: 'Sets reminders',
    desc: 'The assistant creates calendar events and internal reminders weeks before each expiry — enough runway to act before the deadline.',
  },
  {
    icon: <MailOutlineIcon sx={{ fontSize: 20, color: '#f59e0b' }} />,
    iconBg: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
    accent: '#f59e0b',
    title: 'Contacts your provider',
    desc: 'When a renewal window opens, the assistant sends a personalised email to the right contractor for each property.',
  },
  {
    icon: <EventAvailableIcon sx={{ fontSize: 20, color: DESIGN_TOKENS.success }} />,
    iconBg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
    accent: DESIGN_TOKENS.success,
    title: 'Books the appointment',
    desc: 'The assistant manages the conversation with your contractor until an appointment is booked — no missed deadlines, no fines.',
  },
  {
    icon: <VerifiedIcon sx={{ fontSize: 20, color: '#6366f1' }} />,
    iconBg: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)',
    accent: '#6366f1',
    title: 'Renews your record',
    desc: "The provider's renewed certificate is picked up from your inbox, validated, and replaces the old one automatically.",
  },
];

const AIFlowPage2: React.FC = () => {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const navigate = useNavigate();
  const { palette } = useTheme();

  return (
    <Box sx={{ flex: 1, p: { xs: 2, md: 4 } }}>
      <style>{`
        @keyframes flowDown { to { stroke-dashoffset: -20; } }
        @keyframes flowOut  { to { stroke-dashoffset: -12; } }
      `}</style>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Typography
          onClick={() => navigate('/ai-flow')}
          sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#6366f1', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          ← View variant 1
        </Typography>
      </Box>

      <Box sx={{ display: { xs: 'block', lg: 'flex' }, alignItems: 'flex-start' }}>

        {/* ── LEFT 40% ── */}
        <Box sx={{ flex: '0 0 40%', position: { lg: 'sticky' }, top: { lg: 24 }, pt: { xs: 4, lg: 10 }, pr: { lg: 8 }, pl: { xs: 1, lg: 4 }, mb: { xs: 5, lg: 0 } }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#f0f4ff', borderRadius: 10, px: 2, py: 0.6, mb: 3 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: palette.tokens.primary }} />
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: palette.tokens.primary, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Imperial AI Agent
            </Typography>
          </Box>
          <Typography variant="displayXl" sx={{ color: palette.tokens.ink, lineHeight: 1.05, mb: 2.5, letterSpacing: -2, fontSize: { xs: '2.5rem', md: '4.3rem' } }}>
            Your compliance,{' '}
            <Box component="span" sx={{ color: palette.tokens.primary }}>fully automated.</Box>
          </Typography>
          <Typography sx={{ fontSize: '1.1rem', color: palette.tokens.muted, lineHeight: 1.7, mb: 4, maxWidth: 460 }}>
            From uploading your first certificate to renewing the last one — the AI agent handles the entire lifecycle so you can focus on your properties, not your paperwork.
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: palette.tokens.mutedSoft }}>
            Hover over each step to learn more
          </Typography>
        </Box>

        {/* ── RIGHT 60% ── */}
        <Box sx={{ flex: '0 0 60%', maxWidth: '60%', display: 'flex', flexDirection: 'column', alignItems: 'center', pt: { lg: 8 } }}>

          {/* Upload card */}
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            bgcolor: palette.tokens.white, border: `1.5px solid ${palette.tokens.hairline}`, borderRadius: '14px', p: 1.75, width: 308,
            transition: 'all 0.2s',
            '&:hover': { borderColor: '#6366f1', boxShadow: '0 4px 16px rgba(99,102,241,0.12)' },
          }}>
            <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CloudUploadIcon sx={{ fontSize: 22, color: '#6366f1' }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: palette.tokens.mutedSoft, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.2 }}>One-time setup</Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '15px', color: palette.tokens.ink }}>You upload your portfolio</Typography>
            </Box>
          </Box>

          {/* Connector: upload → hub */}
          <Box sx={{ height: CONNECTOR_H, display: 'flex', justifyContent: 'center' }}>
            <svg width="2" height={CONNECTOR_H} style={{ overflow: 'visible' }}>
              <line x1="1" y1="0" x2="1" y2={CONNECTOR_H}
                stroke="#6366f1" strokeWidth="2" strokeDasharray="5 5"
                style={{ animation: 'flowDown 0.7s linear infinite' }} />
            </svg>
          </Box>

          {/* Fan diagram */}
          <Box sx={{ position: 'relative', width: DIAGRAM_W, height: DIAGRAM_H }}>

            {/* Hub card */}
            <Box sx={{
              position: 'absolute',
              left: HUB_LEFT, top: 0,
              width: HUB_W, height: HUB_H,
              bgcolor: palette.tokens.white,
              border: '2px solid #c7d2fe',
              borderRadius: '14px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              zIndex: 2,
              boxShadow: '0 4px 20px rgba(99,102,241,0.14)',
              pointerEvents: 'none',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: '#6366f1' }} />
                <Typography sx={{ fontSize: '12px', fontWeight: 800, color: '#4f46e5', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  AI Copilot
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '10.5px', color: palette.tokens.mutedSoft }}>Zero effort. Every renewal.</Typography>
            </Box>

            {/* SVG arms */}
            <svg width={DIAGRAM_W} height={DIAGRAM_H} style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}>
              {ACTION_STEPS.map((step, i) => {
                const isHov = hoveredIdx === i;
                return (
                  <line
                    key={i}
                    x1={HUB_CX} y1={ARM_Y0}
                    x2={CARD_CXS[i]} y2={CARD_CY}
                    stroke={isHov ? step.accent : '#c7d2fe'}
                    strokeWidth={isHov ? 2 : 1.5}
                    strokeDasharray="6 6"
                    style={{ animation: 'flowOut 0.55s linear infinite', transition: 'stroke 0.2s, stroke-width 0.2s' }}
                  />
                );
              })}
            </svg>

            {/* 5 action cards — expand vertically on hover */}
            {ACTION_STEPS.map((step, i) => {
              const isHov = hoveredIdx === i;
              return (
                <Box
                  key={step.title}
                  onMouseEnter={() => setHoveredIdx(i)}
                  onMouseLeave={() => setHoveredIdx(null)}
                  sx={{
                    position: 'absolute',
                    left: CARD_CXS[i] - CARD_W / 2,
                    top: CARD_TOP,
                    width: CARD_W,
                    bgcolor: palette.tokens.white,
                    border: `1.5px solid ${isHov ? step.accent : palette.tokens.hairline}`,
                    borderRadius: '12px',
                    p: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    zIndex: isHov ? 3 : 1,
                    cursor: 'default',
                    transition: 'border-color 0.2s, box-shadow 0.2s, transform 0.2s',
                    boxShadow: isHov ? `0 6px 20px ${step.accent}35` : '0 1px 4px rgba(0,0,0,0.05)',
                    transform: isHov ? 'translateY(-4px)' : 'none',
                  }}
                >
                  {/* Icon */}
                  <Box sx={{ width: 38, height: 38, borderRadius: '10px', background: step.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.75 }}>
                    {step.icon}
                  </Box>

                  {/* Title */}
                  <Typography sx={{
                    fontSize: '11.5px', fontWeight: 700,
                    color: isHov ? step.accent : palette.tokens.ink,
                    lineHeight: 1.3, textAlign: 'center',
                    transition: 'color 0.2s',
                  }}>
                    {step.title}
                  </Typography>

                  {/* Description — slides in on hover */}
                  <Box sx={{
                    maxHeight: isHov ? '120px' : 0,
                    overflow: 'hidden',
                    transition: 'max-height 0.3s ease',
                  }}>
                    <Typography sx={{ fontSize: '10.5px', color: palette.tokens.muted, lineHeight: 1.55, textAlign: 'center', mt: 1 }}>
                      {step.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AIFlowPage2;
